const DEVICE_IMAGE_SLIDER_VISIBILITY_THRESHOLD = 0.6;
const DEVICE_IMAGE_SLIDER_DESKTOP_QUERY = '(min-width: 768px)';

const initDeviceImageSlider = (slider) => {
  if (slider.dataset.deviceImageSliderReady === 'true') return;

  const track = slider.querySelector('[data-device-image-slider-track]');
  const allSlides = [...slider.querySelectorAll('[data-device-image-slider-slide]')];
  if (!track || allSlides.length === 0) return;

  slider.dataset.deviceImageSliderReady = 'true';

  const allDots = [...slider.querySelectorAll('[data-device-image-slider-dot]')];
  const previousButton = slider.querySelector('[data-device-image-slider-prev]');
  const nextButton = slider.querySelector('[data-device-image-slider-next]');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktopQuery = window.matchMedia(DEVICE_IMAGE_SLIDER_DESKTOP_QUERY);

  // Slides and dots are hidden per device by CSS, so the rendered state is the single source of
  // truth for which ones are live. offsetParent is null for anything display:none.
  const isRendered = (element) => element.offsetParent !== null;

  let slides = [];
  let dots = [];
  let activeIndex = 0;
  let visibilityObserver = null;

  const scrollToIndex = (index) => {
    const slide = slides[index];
    if (!slide) return;

    track.scrollTo({
      left: slide.offsetLeft,
      behavior: reducedMotionQuery.matches ? 'auto' : 'smooth',
    });
  };

  const setActiveIndex = (index) => {
    activeIndex = index;

    allDots.forEach((dot) => {
      const isActive = dots[index] === dot;
      dot.classList.toggle('is-active', isActive);

      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });

    previousButton?.toggleAttribute('disabled', index === 0);
    nextButton?.toggleAttribute('disabled', index === slides.length - 1);
  };

  const refresh = () => {
    visibilityObserver?.disconnect();
    visibilityObserver = null;

    slides = allSlides.filter(isRendered);
    dots = allDots.filter(isRendered);

    // One slide or none is a static image, not a slider.
    if (slides.length < 2) {
      setActiveIndex(0);
      return;
    }

    visibilityObserver = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries.find((entry) => entry.isIntersecting);
        if (!visibleEntry) return;

        setActiveIndex(slides.indexOf(visibleEntry.target));
      },
      { root: track, threshold: DEVICE_IMAGE_SLIDER_VISIBILITY_THRESHOLD }
    );

    slides.forEach((slide) => visibilityObserver.observe(slide));
    setActiveIndex(0);
  };

  allDots.forEach((dot) => {
    dot.addEventListener('click', () => scrollToIndex(dots.indexOf(dot)));
  });

  previousButton?.addEventListener('click', () => scrollToIndex(activeIndex - 1));
  nextButton?.addEventListener('click', () => scrollToIndex(activeIndex + 1));

  slider.querySelector('[data-device-image-slider-dots]')?.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    const nextIndex = event.key === 'ArrowLeft' ? activeIndex - 1 : activeIndex + 1;
    if (nextIndex < 0 || nextIndex > slides.length - 1) return;

    scrollToIndex(nextIndex);
    dots[nextIndex]?.focus();
  });

  desktopQuery.addEventListener('change', refresh);

  refresh();
};

const initAllDeviceImageSliders = (root = document) => {
  root.querySelectorAll('[data-device-image-slider]').forEach(initDeviceImageSlider);
};

document.addEventListener('DOMContentLoaded', () => initAllDeviceImageSliders());

document.addEventListener('shopify:section:load', (event) => {
  initAllDeviceImageSliders(event.target);
});

document.addEventListener('shopify:block:select', (event) => {
  const slide = event.target.closest('[data-device-image-slider-slide]');
  const track = slide?.closest('[data-device-image-slider-track]');
  if (!slide || !track) return;

  track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
});
